/**
 * Country-specific phone number length validation.
 * Keys are ISO2 country codes (lowercase).
 * Values are { min, max } representing the expected LOCAL digit count
 * (excluding the country/dial code).
 */
export const COUNTRY_PHONE_LENGTHS: Record<string, { min: number; max: number }> = {
  // North America (NANP)
  us: { min: 10, max: 10 },
  ca: { min: 10, max: 10 },
  bs: { min: 10, max: 10 }, // Bahamas (NANP)
  pr: { min: 10, max: 10 },

  // Central & South America
  mx: { min: 7, max: 10 }, // min updated to 7, Mexico: has format mask "... ... ...." (10 digits)
  br: { min: 11, max: 11 }, // Brazil: has format mask "(..) ........." (11 digits)
  ar: { min: 6, max: 10 }, // min updated to 6, Argentina: has format mask "(..) ........" (10 digits)
  co: { min: 7, max: 10 }, // min updated to 7, Colombia: has format mask "... ... ...." (10 digits)
  cl: { min: 7, max: 13 }, // min updated to 7, Chile: 15 - 2 (+56) = 13
  pe: { min: 6, max: 13 }, // min updated to 6, Peru: 15 - 2 (+51) = 13
  ve: { min: 7, max: 13 }, // min updated to 7, Venezuela: 15 - 2 (+58) = 13
  ec: { min: 12, max: 12 }, // Ecuador: 15 - 3 (+593) = 12
  bo: { min: 12, max: 12 }, // Bolivia: 15 - 3 (+591) = 12
  py: { min: 12, max: 12 }, // Paraguay: 15 - 3 (+595) = 12
  uy: { min: 12, max: 12 }, // Uruguay: 15 - 3 (+598) = 12
  sr: { min: 12, max: 12 }, // Suriname: 15 - 3 (+597) = 12
  gy: { min: 12, max: 12 }, // Guyana: 15 - 3 (+592) = 12
  gf: { min: 12, max: 12 }, // French Guiana: 15 - 3 (+594) = 12
  ag: { min: 10, max: 10 }, // Antigua and Barbuda (NANP) ✓
  bb: { min: 10, max: 10 }, // Barbados (NANP) ✓
  dm: { min: 10, max: 10 }, // Dominica (NANP) ✓
  do: { min: 10, max: 10 }, // Dominican Republic (NANP) ✓
  gd: { min: 10, max: 10 }, // Grenada (NANP) ✓
  jm: { min: 10, max: 10 }, // Jamaica (NANP) ✓
  kn: { min: 10, max: 10 }, // Saint Kitts and Nevis (NANP) ✓
  lc: { min: 10, max: 10 }, // Saint Lucia (NANP) ✓
  vc: { min: 10, max: 10 }, // Saint Vincent (NANP) ✓
  tt: { min: 10, max: 10 }, // Trinidad and Tobago (NANP) ✓
  cr: { min: 8, max: 8 }, // Costa Rica: has format mask "....-......" (8 digits)
  sv: { min: 8, max: 8 }, // El Salvador: has format mask "....-......" (8 digits)
  gt: { min: 8, max: 8 }, // Guatemala: has format mask "....-......" (8 digits)
  hn: { min: 12, max: 12 }, // Honduras: 15 - 3 (+504) = 12
  ni: { min: 12, max: 12 }, // Nicaragua: 15 - 3 (+505) = 12
  pa: { min: 12, max: 12 }, // Panama: 15 - 3 (+507) = 12
  bz: { min: 12, max: 12 }, // Belize: 15 - 3 (+501) = 12
  ht: { min: 8, max: 8 }, // Haiti: has format mask "....-......" (8 digits)
  cw: { min: 12, max: 12 }, // Curaçao: 15 - 3 (+599) = 12
  aw: { min: 12, max: 12 }, // Aruba: 15 - 3 (+297) = 12
  gp: { min: 12, max: 12 }, // Guadeloupe: 15 - 3 (+590) = 12
  mq: { min: 12, max: 12 }, // Martinique: 15 - 3 (+596) = 12
  pf: { min: 12, max: 12 }, // French Polynesia: 15 - 3 (+689) = 12

  // Europe
  gb: { min: 9, max: 10 }, // min updated to 9, has format mask ".... ......"
  de: { min: 7, max: 12 }, // min updated to 7, has format mask ".... ........"
  fr: { min: 9, max: 9 }, // has format mask ". .. .. .. .."
  it: { min: 6, max: 10 }, // min updated to 6, has format mask "... ......."
  es: { min: 9, max: 9 }, // has format mask "... ... ..."
  pt: { min: 9, max: 12 }, // min updated to 9, Portugal: 15 - 3 (+351) = 12
  nl: { min: 9, max: 10 }, // min updated to 9, has format mask ".. ........"
  be: { min: 8, max: 9 }, // min updated to 8, has format mask "... .. .. .."
  ch: { min: 7, max: 9 }, // min updated to 7, has format mask ".. ... .. .."
  at: { min: 7, max: 13 }, // min updated to 7, Austria: 15 - 2 (+43) = 13
  se: { min: 7, max: 9 }, // min updated to 7, has format mask "(...) ...-..."
  no: { min: 8, max: 8 }, // has format mask "... .. ..."
  dk: { min: 8, max: 8 }, // has format mask ".. .. .. .."
  fi: { min: 7, max: 9 }, // min updated to 7, Finland: has format mask ".. ... .. .." (9 digits)
  pl: { min: 7, max: 9 }, // min updated to 7, has format mask "...-...-..."
  ie: { min: 7, max: 9 }, // min updated to 7, has format mask ".. ......."
  gr: { min: 10, max: 13 }, // min updated to 10, Greece: 15 - 2 (+30) = 13
  cz: { min: 9, max: 9 }, // has format mask "... ... ..."
  ro: { min: 6, max: 13 }, // min updated to 6, Romania: 15 - 2 (+40) = 13
  hu: { min: 8, max: 13 }, // min updated to 8, Hungary: 15 - 2 (+36) = 13
  tr: { min: 7, max: 10 }, // min updated to 7, has format mask "... ... .. .."
  ru: { min: 10, max: 10 }, // has format mask "(...) ...-..-.."
  ua: { min: 9, max: 9 }, // has format mask "(..) ... .. .."
  bg: { min: 7, max: 12 }, // min updated to 7, Bulgaria: 15 - 3 (+359) = 12
  hr: { min: 8, max: 12 }, // min updated to 8, Croatia: 15 - 3 (+385) = 12
  rs: { min: 6, max: 12 }, // min updated to 6, Serbia: 15 - 3 (+381) = 12
  si: { min: 7, max: 12 }, // min updated to 7, Slovenia: 15 - 3 (+386) = 12
  sk: { min: 8, max: 12 }, // min updated to 8, Slovakia: 15 - 3 (+421) = 12
  lt: { min: 12, max: 12 }, // Lithuania: 15 - 3 (+370) = 12
  lv: { min: 8, max: 8 }, // Latvia: has format mask ".. ... ..." (8 digits)
  ee: { min: 7, max: 10 }, // min updated to 7, Estonia: has format mask ".... ......" (10 digits)
  by: { min: 7, max: 9 }, // min updated to 7, Belarus: has format mask "(..) ... .. .." (9 digits)
  md: { min: 7, max: 8 }, // min updated to 7, Moldova: has format mask "(..) ..-..-.." (8 digits)
  al: { min: 12, max: 12 }, // Albania: 15 - 3 (+355) = 12
  mk: { min: 12, max: 12 }, // Macedonia: 15 - 3 (+389) = 12
  me: { min: 12, max: 12 }, // Montenegro: 15 - 3 (+382) = 12
  ba: { min: 12, max: 12 }, // Bosnia: 15 - 3 (+387) = 12
  cy: { min: 8, max: 8 }, // Cyprus: has format mask ".. ......" (8 digits)
  mt: { min: 7, max: 12 }, // min updated to 7, Malta: 15 - 3 (+356) = 12
  is: { min: 7, max: 7 }, // Iceland: has format mask "... ...." (7 digits)
  lu: { min: 12, max: 12 }, // Luxembourg: 15 - 3 (+352) = 12
  li: { min: 12, max: 12 }, // Liechtenstein: 15 - 3 (+423) = 12
  mc: { min: 12, max: 12 }, // Monaco: 15 - 3 (+377) = 12
  sm: { min: 12, max: 12 }, // San Marino: 15 - 3 (+378) = 12
  va: { min: 10, max: 10 }, // Vatican: has format mask ".. .... ...." (10 digits)
  ad: { min: 12, max: 12 }, // Andorra: 15 - 3 (+376) = 12
  xk: { min: 12, max: 12 }, // Kosovo: 15 - 3 (+383) = 12

  // GCC / Middle East
  ae: { min: 7, max: 12 }, // min updated to 7, UAE: 15 - 3 (+971) = 12
  sa: { min: 7, max: 12 }, // min updated to 7, Saudi Arabia: 15 - 3 (+966) = 12
  kw: { min: 12, max: 12 }, // Kuwait: 15 - 3 (+965) = 12
  qa: { min: 12, max: 12 }, // Qatar: 15 - 3 (+974) = 12
  bh: { min: 12, max: 12 }, // Bahrain: 15 - 3 (+973) = 12
  om: { min: 12, max: 12 }, // Oman: 15 - 3 (+968) = 12
  iq: { min: 12, max: 12 }, // Iraq: 15 - 3 (+964) = 12
  jo: { min: 12, max: 12 }, // Jordan: 15 - 3 (+962) = 12
  lb: { min: 12, max: 12 }, // Lebanon: 15 - 3 (+961) = 12
  il: { min: 7, max: 10 }, // min updated to 7, Israel: has format mask "... ... ...." (10 digits)
  sy: { min: 12, max: 12 }, // Syria: 15 - 3 (+963) = 12
  ye: { min: 12, max: 12 }, // Yemen: 15 - 3 (+967) = 12
  ps: { min: 12, max: 12 }, // Palestine: 15 - 3 (+970) = 12
  ir: { min: 10, max: 10 }, // Iran: has format mask "... ... ...." (10 digits)

  // South Asia
  in: { min: 10, max: 10 }, // India: has format mask ".....-....." (10 digits)
  pk: { min: 7, max: 10 }, // min updated to 7, Pakistan: has format mask "...-......." (10 digits)
  bd: { min: 7, max: 12 }, // min updated to 7, Bangladesh: 15 - 3 (+880) = 12
  lk: { min: 13, max: 13 }, // Sri Lanka: 15 - 2 (+94) = 13
  np: { min: 12, max: 12 }, // Nepal: 15 - 3 (+977) = 12
  bt: { min: 12, max: 12 }, // Bhutan: 15 - 3 (+975) = 12
  mv: { min: 12, max: 12 }, // Maldives: 15 - 3 (+960) = 12
  af: { min: 13, max: 13 }, // Afghanistan: 15 - 2 (+93) = 13 ✓

  // East & Southeast Asia
  cn: { min: 8, max: 11 }, // min updated to 8, China: has format mask "..-........." (11 digits)
  jp: { min: 9, max: 10 }, // min updated to 9, Japan: has format mask ".. .... ...." (10 digits)
  kr: { min: 7, max: 11 }, // min updated to 7, South Korea: has format mask "... .... ...." (11 digits)
  ph: { min: 7, max: 11 }, // min updated to 7, Philippines: has format mask ".... ......." (11 digits)
  id: { min: 8, max: 13 }, // min updated to 8, Indonesia: 15 - 2 (+62) = 13
  my: { min: 7, max: 10 }, // min updated to 7, Malaysia: has format mask "..-....-......" (10 digits)
  sg: { min: 8, max: 8 }, // Singapore: has format mask "....-......" (8 digits)
  th: { min: 8, max: 13 }, // min updated to 8, Thailand: 15 - 2 (+66) = 13
  vn: { min: 9, max: 13 }, // min updated to 9, Vietnam: 15 - 2 (+84) = 13
  hk: { min: 8, max: 8 }, // Hong Kong: has format mask ".... ...." (8 digits)
  tw: { min: 8, max: 12 }, // min updated to 8, Taiwan: 15 - 3 (+886) = 12
  kh: { min: 12, max: 12 }, // Cambodia: 15 - 3 (+855) = 12
  la: { min: 12, max: 12 }, // Laos: 15 - 3 (+856) = 12
  mm: { min: 13, max: 13 }, // Myanmar: 15 - 2 (+95) = 13
  bn: { min: 12, max: 12 }, // Brunei: 15 - 3 (+673) = 12
  tl: { min: 12, max: 12 }, // Timor-Leste: 15 - 3 (+670) = 12
  mo: { min: 12, max: 12 }, // Macau: 15 - 3 (+853) = 12
  kp: { min: 12, max: 12 }, // North Korea: 15 - 3 (+850) = 12
  mn: { min: 12, max: 12 }, // Mongolia: 15 - 3 (+976) = 12

  // Africa
  eg: { min: 8, max: 13 }, // min updated to 8, Egypt: 15 - 2 (+20) = 13
  ng: { min: 7, max: 12 }, // min updated to 7, Nigeria: 15 - 3 (+234) = 12
  za: { min: 9, max: 13 }, // min updated to 9, South Africa: 15 - 2 (+27) = 13
  ke: { min: 9, max: 12 }, // min updated to 9, Kenya: 15 - 3 (+254) = 12
  gh: { min: 12, max: 12 }, // Ghana: 15 - 3 (+233) = 12
  tz: { min: 12, max: 12 }, // Tanzania: 15 - 3 (+255) = 12
  et: { min: 12, max: 12 }, // Ethiopia: 15 - 3 (+251) = 12
  ma: { min: 12, max: 12 }, // Morocco: 15 - 3 (+212) = 12
  mg: { min: 12, max: 12 }, // Madagascar: 15 - 3 (+261) = 12
  dz: { min: 12, max: 12 }, // Algeria: 15 - 3 (+213) = 12
  tn: { min: 12, max: 12 }, // Tunisia: 15 - 3 (+216) = 12
  ug: { min: 12, max: 12 }, // Uganda: 15 - 3 (+256) = 12
  ao: { min: 12, max: 12 }, // Angola: 15 - 3 (+244) = 12
  mz: { min: 12, max: 12 }, // Mozambique: 15 - 3 (+258) = 12
  cm: { min: 12, max: 12 }, // Cameroon: 15 - 3 (+237) = 12
  ci: { min: 8, max: 8 }, // Côte d'Ivoire: has format mask ".. .. .. .." (8 digits)
  ne: { min: 12, max: 12 }, // Niger: 15 - 3 (+227) = 12
  bf: { min: 12, max: 12 }, // Burkina Faso: 15 - 3 (+226) = 12
  ml: { min: 12, max: 12 }, // Mali: 15 - 3 (+223) = 12
  mw: { min: 12, max: 12 }, // Malawi: 15 - 3 (+265) = 12
  zm: { min: 12, max: 12 }, // Zambia: 15 - 3 (+260) = 12
  sn: { min: 12, max: 12 }, // Senegal: 15 - 3 (+221) = 12
  so: { min: 12, max: 12 }, // Somalia: 15 - 3 (+252) = 12
  td: { min: 12, max: 12 }, // Chad: 15 - 3 (+235) = 12
  rw: { min: 12, max: 12 }, // Rwanda: 15 - 3 (+250) = 12
  bj: { min: 12, max: 12 }, // Benin: 15 - 3 (+229) = 12
  gn: { min: 12, max: 12 }, // Guinea: 15 - 3 (+224) = 12
  bi: { min: 12, max: 12 }, // Burundi: 15 - 3 (+257) = 12
  ss: { min: 12, max: 12 }, // South Sudan: 15 - 3 (+211) = 12
  er: { min: 12, max: 12 }, // Eritrea: 15 - 3 (+291) = 12
  mr: { min: 12, max: 12 }, // Mauritania: 15 - 3 (+222) = 12
  lr: { min: 12, max: 12 }, // Liberia: 15 - 3 (+231) = 12
  sl: { min: 12, max: 12 }, // Sierra Leone: 15 - 3 (+232) = 12
  tg: { min: 12, max: 12 }, // Togo: 15 - 3 (+228) = 12
  ly: { min: 12, max: 12 }, // Libya: 15 - 3 (+218) = 12
  cg: { min: 12, max: 12 }, // Congo: 15 - 3 (+242) = 12
  cd: { min: 12, max: 12 }, // Congo (DRC): 15 - 3 (+243) = 12
  cf: { min: 12, max: 12 }, // Central African Republic: 15 - 3 (+236) = 12
  ga: { min: 12, max: 12 }, // Gabon: 15 - 3 (+241) = 12
  gq: { min: 12, max: 12 }, // Equatorial Guinea: 15 - 3 (+240) = 12
  gw: { min: 12, max: 12 }, // Guinea-Bissau: 15 - 3 (+245) = 12
  gm: { min: 12, max: 12 }, // Gambia: 15 - 3 (+220) = 12
  bw: { min: 12, max: 12 }, // Botswana: 15 - 3 (+267) = 12
  mu: { min: 12, max: 12 }, // Mauritius: 15 - 3 (+230) = 12
  sz: { min: 12, max: 12 }, // Swaziland: 15 - 3 (+268) = 12
  ls: { min: 12, max: 12 }, // Lesotho: 15 - 3 (+266) = 12
  na: { min: 12, max: 12 }, // Namibia: 15 - 3 (+264) = 12
  sc: { min: 12, max: 12 }, // Seychelles: 15 - 3 (+248) = 12
  sd: { min: 12, max: 12 }, // Sudan: 15 - 3 (+249) = 12
  cv: { min: 12, max: 12 }, // Cape Verde: 15 - 3 (+238) = 12
  dj: { min: 12, max: 12 }, // Djibouti: 15 - 3 (+253) = 12
  km: { min: 12, max: 12 }, // Comoros: 15 - 3 (+269) = 12
  st: { min: 12, max: 12 }, // São Tomé and Príncipe: 15 - 3 (+239) = 12
  zw: { min: 12, max: 12 }, // Zimbabwe: 15 - 3 (+263) = 12

  // Oceania
  au: { min: 10, max: 10 }, // Australia: has format mask "(..) .... ...." (10 digits)
  nz: { min: 10, max: 10 }, // New Zealand: has format mask "...-...-......" (10 digits)
  fj: { min: 12, max: 12 }, // Fiji: 15 - 3 (+679) = 12
  pg: { min: 12, max: 12 }, // Papua New Guinea: 15 - 3 (+675) = 12
  sb: { min: 12, max: 12 }, // Solomon Islands: 15 - 3 (+677) = 12
  vu: { min: 12, max: 12 }, // Vanuatu: 15 - 3 (+678) = 12
  nc: { min: 12, max: 12 }, // New Caledonia: 15 - 3 (+687) = 12
  ws: { min: 12, max: 12 }, // Samoa: 15 - 3 (+685) = 12
  ki: { min: 12, max: 12 }, // Kiribati: 15 - 3 (+686) = 12
  to: { min: 12, max: 12 }, // Tonga: 15 - 3 (+676) = 12
  fm: { min: 12, max: 12 }, // Micronesia: 15 - 3 (+691) = 12
  pw: { min: 12, max: 12 }, // Palau: 15 - 3 (+680) = 12
  mh: { min: 12, max: 12 }, // Marshall Islands: 15 - 3 (+692) = 12
  nr: { min: 12, max: 12 }, // Nauru: 15 - 3 (+674) = 12
  tv: { min: 12, max: 12 }, // Tuvalu: 15 - 3 (+688) = 12

  // Other territories
  gu: { min: 10, max: 10 }, // Guam (NANP) ✓
  io: { min: 12, max: 12 }, // British Indian Ocean Territory: 15 - 3 (+246) = 12
  bq: { min: 12, max: 12 }, // Caribbean Netherlands: 15 - 3 (+599) = 12
  re: { min: 12, max: 12 }, // Réunion: 15 - 3 (+262) = 12
  kz: { min: 10, max: 10 }, // Kazakhstan: has format mask "... ...-..-.." (10 digits)
  uz: { min: 9, max: 9 }, // Uzbekistan: has format mask ".. ... .. .." (9 digits)
  tm: { min: 12, max: 12 }, // Turkmenistan: 15 - 3 (+993) = 12
  tj: { min: 12, max: 12 }, // Tajikistan: 15 - 3 (+992) = 12
  kg: { min: 9, max: 9 }, // Kyrgyzstan: has format mask "... ... ..." (9 digits)
  ge: { min: 12, max: 12 }, // Georgia: 15 - 3 (+995) = 12
  am: { min: 8, max: 8 }, // Armenia: has format mask ".. ......" (8 digits)
  az: { min: 9, max: 9 }, // Azerbaijan: has format mask "(..) ... .. .." (9 digits)
  cu: { min: 13, max: 13 }, // Cuba: 15 - 2 (+53) = 13
};

// Reverse lookup: dial code (digits only) → ISO2. For shared codes (e.g. +1),
// picks the primary country. This is used in edit mode to resolve the ISO2
// from a stored phone_code when PhoneInput hasn't fired onChange yet.
export const DIAL_CODE_TO_ISO2: Record<string, string> = {
  // NANP countries (all share +1, primary is US)
  "1": "us",
  "1268": "ag", // Antigua and Barbuda
  "1246": "bb", // Barbados
  "1767": "dm", // Dominica
  "1473": "gd", // Grenada
  "1876": "jm", // Jamaica
  "1869": "kn", // Saint Kitts and Nevis
  "1758": "lc", // Saint Lucia
  "1784": "vc", // Saint Vincent and the Grenadines
  "1868": "tt", // Trinidad and Tobago
  "1242": "bs", // Bahamas
  "1787": "pr", // Puerto Rico
  "1809": "do", // Dominican Republic
  "1829": "do", // Dominican Republic (overlay)
  "1849": "do", // Dominican Republic (overlay)
  "1671": "gu", // Guam

  // Americas
  "52": "mx",
  "55": "br",
  "54": "ar",
  "57": "co",
  "56": "cl",
  "51": "pe",
  "58": "ve",
  "593": "ec",
  "591": "bo",
  "595": "py",
  "598": "uy",
  "597": "sr",
  "592": "gy",
  "594": "gf",
  "506": "cr",
  "503": "sv",
  "502": "gt",
  "504": "hn",
  "505": "ni",
  "507": "pa",
  "501": "bz",
  "509": "ht",
  "599": "cw", // Curaçao (also Caribbean Netherlands)
  "297": "aw",
  "590": "gp",
  "596": "mq",
  "689": "pf",
  "53": "cu",

  // Europe
  "44": "gb",
  "49": "de",
  "33": "fr",
  "39": "it",
  "34": "es",
  "351": "pt",
  "31": "nl",
  "32": "be",
  "41": "ch",
  "43": "at",
  "46": "se",
  "47": "no",
  "45": "dk",
  "358": "fi",
  "48": "pl",
  "353": "ie",
  "30": "gr",
  "420": "cz",
  "40": "ro",
  "36": "hu",
  "90": "tr",
  "7": "ru", // Russia (also Kazakhstan)
  "380": "ua",
  "359": "bg",
  "385": "hr",
  "381": "rs",
  "386": "si",
  "421": "sk",
  "370": "lt",
  "371": "lv",
  "372": "ee",
  "375": "by",
  "373": "md",
  "355": "al",
  "389": "mk",
  "382": "me",
  "387": "ba",
  "357": "cy",
  "356": "mt",
  "354": "is",
  "352": "lu",
  "423": "li",
  "377": "mc",
  "378": "sm",
  "379": "va",
  "376": "ad",
  "383": "xk",

  // Middle East
  "971": "ae",
  "966": "sa",
  "965": "kw",
  "974": "qa",
  "973": "bh",
  "968": "om",
  "964": "iq",
  "962": "jo",
  "961": "lb",
  "972": "il",
  "963": "sy",
  "967": "ye",
  "970": "ps",
  "98": "ir",

  // South Asia
  "91": "in",
  "92": "pk",
  "880": "bd",
  "94": "lk",
  "977": "np",
  "975": "bt",
  "960": "mv",
  "93": "af",

  // East & Southeast Asia
  "86": "cn",
  "81": "jp",
  "82": "kr",
  "63": "ph",
  "62": "id",
  "60": "my",
  "65": "sg",
  "66": "th",
  "84": "vn",
  "852": "hk",
  "886": "tw",
  "855": "kh",
  "856": "la",
  "95": "mm",
  "673": "bn",
  "670": "tl",
  "853": "mo",
  "850": "kp",
  "976": "mn",

  // Africa
  "20": "eg",
  "234": "ng",
  "27": "za",
  "254": "ke",
  "233": "gh",
  "255": "tz",
  "251": "et",
  "212": "ma",
  "261": "mg", // Madagascar
  "213": "dz",
  "216": "tn",
  "256": "ug",
  "244": "ao",
  "258": "mz",
  "237": "cm",
  "225": "ci",
  "227": "ne",
  "226": "bf",
  "223": "ml",
  "265": "mw",
  "260": "zm",
  "263": "zw", // Zimbabwe
  "221": "sn",
  "252": "so",
  "235": "td",
  "250": "rw",
  "229": "bj",
  "224": "gn",
  "257": "bi",
  "211": "ss",
  "291": "er",
  "222": "mr",
  "231": "lr",
  "232": "sl",
  "228": "tg",
  "218": "ly",
  "242": "cg",
  "243": "cd",
  "236": "cf",
  "241": "ga",
  "240": "gq",
  "245": "gw",
  "220": "gm",
  "267": "bw",
  "230": "mu",
  "268": "sz",
  "266": "ls",
  "264": "na",
  "248": "sc",
  "249": "sd", // Sudan
  "238": "cv",
  "253": "dj",
  "269": "km",
  "239": "st",

  // Oceania
  "61": "au",
  "64": "nz",
  "679": "fj",
  "675": "pg",
  "677": "sb",
  "678": "vu",
  "687": "nc",
  "685": "ws",
  "686": "ki",
  "676": "to",
  "691": "fm",
  "680": "pw",
  "692": "mh",
  "674": "nr",
  "688": "tv",

  // Other territories
  "246": "io",
  "262": "re",
  "996": "kg",
  "998": "uz",
  "993": "tm",
  "992": "tj",
  "995": "ge",
  "374": "am",
  "994": "az",
};

export const getIso2FromDialCode = (dialCode: string): string => {
  const digits = dialCode.replace(/\D/g, "");
  return DIAL_CODE_TO_ISO2[digits] || "us";
};

// Default range for countries not in the explicit map
const FALLBACK_LENGTH = { min: 4, max: 13 };

export interface PhoneValidationResult {
  valid: boolean;
  expectedMin: number;
  expectedMax: number;
  actualLength: number;
}

export const validatePhoneNumber = (
  fullPhoneValue: string,
  dialCode: string,
  countryIso2: string,
): PhoneValidationResult => {
  const digits = fullPhoneValue.replace(/\D/g, "");
  const dialDigits = dialCode.replace(/\D/g, "");

  // Extract local number by stripping dial code prefix
  let localDigits = digits;
  if (dialDigits && digits.startsWith(dialDigits)) {
    localDigits = digits.substring(dialDigits.length);
  }

  const iso = countryIso2.toLowerCase();
  const { min, max } = COUNTRY_PHONE_LENGTHS[iso] || FALLBACK_LENGTH;
  const actualLength = localDigits.length;

  return {
    valid: actualLength >= min && actualLength <= max,
    expectedMin: min,
    expectedMax: max,
    actualLength,
  };
};
